import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { useMemo, useRef } from "react";
import { FileDoc, FilePdf, FilePpt, FileXls } from "@phosphor-icons/react";
import { BackIcon, FileIcon, UploadIcon } from "../../icons";
const getSeeMoreEntryKey = (sectionId) => `action:see-more:${sectionId}`;
const getBackEntryKey = (sectionId) => `action:back:${sectionId}`;
const getItemEntryKey = (sectionId, itemId) => `item:${sectionId}:${itemId}`;
const getContextActionEntryKey = (sectionId, itemId) => `action:context:${sectionId}:${itemId}`;
const getUploadEntryKey = (sectionId) => `action:upload:${sectionId}`;
const PRESENTATION_EXTENSIONS = new Set(["ppt", "pptx", "pptm"]);
const SPREADSHEET_EXTENSIONS = new Set(["xls", "xlsx", "xlsm", "csv"]);
const DOCUMENT_EXTENSIONS = new Set(["doc", "docx", "txt", "rtf"]);
const PDF_EXTENSIONS = new Set(["pdf"]);
const getMentionFileExtension = (option) => {
    const fileType = option.fileType?.trim();
    if (fileType) {
        return fileType.startsWith(".")
            ? fileType.slice(1).toLowerCase()
            : fileType.toLowerCase();
    }
    const label = (option.displayLabel || option.label || "").trim();
    const lastDotIndex = label.lastIndexOf(".");
    if (lastDotIndex <= 0 || lastDotIndex === label.length - 1) {
        return "";
    }
    return label.slice(lastDotIndex + 1).toLowerCase();
};
const renderCategoryIcon = (category, option, iconSizePx) => {
    if (category === "presentation" || option.source === "ppt") {
        return (_jsxs("svg", { width: iconSizePx, height: iconSizePx, viewBox: "0 0 24 24", fill: "none", children: [_jsx("path", { d: "M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z", fill: "#D24726" }), _jsx("path", { d: "M14 2L20 8H14V2Z", fill: "#FF8F6B" }), _jsx("path", { d: "M8 11H12C13.1046 11 14 11.8954 14 13C14 14.1046 13.1046 15 12 15H10V18H8V11Z", fill: "white" })] }));
    }
    if (category === "cellRange") {
        return (_jsxs("svg", { width: iconSizePx, height: iconSizePx, viewBox: "0 0 16 16", fill: "none", children: [_jsx("rect", { x: "2", y: "2", width: "12", height: "12", rx: "1.5", stroke: "#16A34A", strokeWidth: "1.2" }), _jsx("path", { d: "M2 6H14M2 10H14M6 2V14M10 2V14", stroke: "#16A34A", strokeWidth: "1.2" })] }));
    }
    if (category === "chart") {
        return (_jsxs("svg", { width: iconSizePx, height: iconSizePx, viewBox: "0 0 16 16", fill: "none", children: [_jsx("path", { d: "M2.5 13.5H13.5", stroke: "#2563EB", strokeWidth: "1.2", strokeLinecap: "round" }), _jsx("rect", { x: "3.5", y: "8.5", width: "2.2", height: "4", rx: "0.6", fill: "#2563EB" }), _jsx("rect", { x: "6.9", y: "5.5", width: "2.2", height: "7", rx: "0.6", fill: "#2563EB" }), _jsx("rect", { x: "10.3", y: "3.5", width: "2.2", height: "9", rx: "0.6", fill: "#2563EB" })] }));
    }
    if (category === "sheet") {
        return (_jsxs("svg", { width: iconSizePx, height: iconSizePx, viewBox: "0 0 16 16", fill: "none", children: [_jsx("rect", { x: "2", y: "2", width: "12", height: "12", rx: "1.5", stroke: "#0F766E", strokeWidth: "1.2" }), _jsx("path", { d: "M2 5H14", stroke: "#0F766E", strokeWidth: "1.2" }), _jsx("path", { d: "M5.5 5V14M10.5 5V14", stroke: "#0F766E", strokeWidth: "1.2" })] }));
    }
    const extension = getMentionFileExtension(option);
    if (PDF_EXTENSIONS.has(extension)) {
        return _jsx(FilePdf, { size: iconSizePx, color: "#DC2626", weight: "duotone" });
    }
    if (PRESENTATION_EXTENSIONS.has(extension)) {
        return _jsx(FilePpt, { size: iconSizePx, color: "#D24726", weight: "duotone" });
    }
    if (SPREADSHEET_EXTENSIONS.has(extension)) {
        return _jsx(FileXls, { size: iconSizePx, color: "#16A34A", weight: "duotone" });
    }
    if (DOCUMENT_EXTENSIONS.has(extension)) {
        return _jsx(FileDoc, { size: iconSizePx, color: "#2563EB", weight: "duotone" });
    }
    return _jsx(FileIcon, { size: iconSizePx, color: "#6b7280" });
};
export const MentionMenu = ({ isOpen, position, sections, contextActions = [], placement = "above", maxHeight = 320, sizeVariant = "default", selectedIndex = 0, onSelectedIndexChange, selectedEntryKey = null, onEntryHover, activeSectionId = null, onSelect, onClose, onSeeMore, onBack, onUploadClick, isLoading = false, }) => {
    const menuRef = useRef(null);
    const isCompact = sizeVariant === "compact";
    const iconSizePx = isCompact ? 14 : 16;
    const sectionHeaderFontSizePx = isCompact ? "10px" : "12px";
    const itemFontSizePx = isCompact ? "11px" : "13px";
    const itemMinHeight = isCompact ? "26px" : "32px";
    const menuHorizontalPaddingPx = isCompact ? 3 : 4;
    const sectionRowGapPx = isCompact ? 2 : 4;
    const sectionRowPaddingTopPx = isCompact ? 6 : 8;
    const sectionRowPaddingLeftPx = isCompact ? 2 : 4;
    const sectionRowPaddingRightPx = isCompact ? 2 : 4;
    const itemRowGapPx = isCompact ? 3 : 4;
    const itemRowPaddingXPx = isCompact ? 2 : 4;
    const itemRowPaddingYPx = isCompact ? 5 : 8;
    const itemRowBorderRadiusPx = isCompact ? 6 : 8;
    const itemIconBoxWidthPx = isCompact ? 14 : 16;
    const itemIconBoxHeightPx = isCompact ? 16 : 20;
    const rowLineHeight = isCompact ? "1.4" : "1.5";
    const seeMorePillHorizontalPaddingPx = isCompact ? 6 : 8;
    const seeMorePillVerticalPaddingPx = isCompact ? 1 : 2;
    const seeMorePillRadiusPx = isCompact ? 6 : 8;
    const seeMoreNudgeRightPx = isCompact ? 2 : 3;
    const backButtonSizePx = isCompact ? 20 : 22;
    const backIconSizePx = isCompact ? 12 : 14;
    const selectedTagFontSizePx = isCompact ? "9px" : "10px";
    const selectedTagPadding = isCompact ? "1px 5px" : "2px 6px";
    const selectedTagReservePx = isCompact ? 128 : 148;
    const contextActionByCategory = useMemo(() => {
        const actionMap = new Map();
        contextActions.forEach((action) => {
            if (!action.category) {
                return;
            }
            if (!actionMap.has(action.category)) {
                actionMap.set(action.category, action);
            }
        });
        return actionMap;
    }, [contextActions]);
    const itemCount = useMemo(() => {
        return sections.reduce((sum, section) => {
            let extraRows = 0;
            if (contextActionByCategory.get(section.category)) {
                extraRows += 1;
            }
            if (section.category === "file" && onUploadClick) {
                extraRows += 1;
            }
            return sum + section.items.length + extraRows;
        }, 0);
    }, [contextActionByCategory, onUploadClick, sections]);
    React.useEffect(() => {
        if (!isOpen)
            return undefined;
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                if (event.defaultPrevented) {
                    return;
                }
                if (activeSectionId) {
                    event.preventDefault();
                    onBack?.(activeSectionId);
                    return;
                }
                onClose();
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [activeSectionId, isOpen, onBack, onClose]);
    React.useEffect(() => {
        if (!isOpen || !menuRef.current) {
            return undefined;
        }
        const rafId = window.requestAnimationFrame(() => {
            const menuNode = menuRef.current;
            if (!menuNode) {
                return;
            }
            if (placement === "below") {
                menuNode.scrollTop = menuNode.scrollHeight;
            }
            else {
                menuNode.scrollTop = 0;
            }
        });
        return () => window.cancelAnimationFrame(rafId);
    }, [activeSectionId, contextActions, isOpen, placement, sections]);
    if (!isOpen)
        return null;
    let runningIndex = 0;
    const isSectionMode = !!activeSectionId;
    return (_jsxs("div", { ref: menuRef, className: "mention-menu-container scrollbar-hidden absolute z-50 bg-white border border-gray-300 rounded-lg overflow-hidden", style: {
            ...(placement === "above"
                ? { bottom: `calc(100% + ${Math.abs(position.top)}px)` }
                : { top: `calc(100% + ${Math.abs(position.top)}px)` }),
            left: `${position.left}px`,
            width: "100%",
            maxWidth: "483px",
            maxHeight: `${maxHeight}px`,
            overflowY: "auto",
            boxShadow: "0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)",
            padding: `0 ${menuHorizontalPaddingPx}px`,
        }, children: [sections.map((section) => (_jsxs(React.Fragment, { children: [_jsxs("div", { className: "flex items-center", style: {
                            columnGap: `${sectionRowGapPx}px`,
                            paddingLeft: sectionRowPaddingLeftPx,
                            paddingRight: sectionRowPaddingRightPx,
                            paddingTop: sectionRowPaddingTopPx,
                        }, children: [isSectionMode && activeSectionId === section.id && (_jsx("button", { type: "button", className: `inline-flex items-center justify-center border-0 rounded-md cursor-pointer transition-colors ${selectedEntryKey === getBackEntryKey(section.id)
                                    ? "bg-gray-200"
                                    : "bg-transparent hover:bg-gray-100"}`, style: {
                                    width: `${backButtonSizePx}px`,
                                    height: `${backButtonSizePx}px`,
                                    flexShrink: 0,
                                }, onMouseDown: (event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onBack?.(section.id);
                                }, onMouseEnter: () => {
                                    onEntryHover?.(getBackEntryKey(section.id));
                                }, "aria-label": `Back to all ${section.title.toLowerCase()}`, children: _jsx(BackIcon, { size: backIconSizePx, color: "#4B5563" }) })), _jsx("h2", { className: "flex-1 font-bold text-gray-500", style: {
                                    fontFamily: "Mulish, sans-serif",
                                    fontSize: sectionHeaderFontSizePx,
                                    lineHeight: rowLineHeight,
                                }, children: section.title }), !isSectionMode && section.totalCount > section.items.length && (_jsx("button", { type: "button", className: `border-0 cursor-pointer transition-colors ${selectedEntryKey === getSeeMoreEntryKey(section.id)
                                    ? "bg-gray-200 text-gray-800"
                                    : "bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-800"}`, style: {
                                    fontFamily: "Mulish, sans-serif",
                                    fontSize: sectionHeaderFontSizePx,
                                    lineHeight: rowLineHeight,
                                    fontWeight: selectedEntryKey === getSeeMoreEntryKey(section.id)
                                        ? 700
                                        : 500,
                                    borderRadius: `${seeMorePillRadiusPx}px`,
                                    padding: `${seeMorePillVerticalPaddingPx}px ${seeMorePillHorizontalPaddingPx}px`,
                                    marginRight: `-${seeMoreNudgeRightPx}px`,
                                }, onMouseDown: (event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onSeeMore?.(section.id);
                                }, onMouseEnter: () => {
                                    onEntryHover?.(getSeeMoreEntryKey(section.id));
                                }, children: "See more" }))] }), section.items.map((option) => {
                        const globalIndex = runningIndex;
                        runningIndex += 1;
                        const itemEntryKey = getItemEntryKey(section.id, option.id);
                        const isSelected = selectedEntryKey
                            ? selectedEntryKey === itemEntryKey
                            : globalIndex === selectedIndex;
                        const category = option.category || section.category;
                        return (_jsxs("div", { className: `flex items-center cursor-pointer transition-colors ${isSelected ? "bg-gray-200" : "hover:bg-gray-100"}`, style: {
                                minHeight: itemMinHeight,
                                columnGap: `${itemRowGapPx}px`,
                                padding: `${itemRowPaddingYPx}px ${itemRowPaddingXPx}px`,
                                borderRadius: `${itemRowBorderRadiusPx}px`,
                            }, onMouseDown: (event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onSelect(option);
                            }, onMouseEnter: () => {
                                if (onEntryHover) {
                                    onEntryHover(itemEntryKey);
                                    return;
                                }
                                onSelectedIndexChange?.(globalIndex);
                            }, children: [_jsx("div", { className: "flex items-center justify-center", style: {
                                        width: itemIconBoxWidthPx,
                                        height: itemIconBoxHeightPx,
                                    }, children: renderCategoryIcon(category, option, iconSizePx) }), _jsx("span", { className: "flex-1 font-bold text-gray-990 truncate", style: {
                                        fontFamily: "Mulish, sans-serif",
                                        fontSize: itemFontSizePx,
                                        lineHeight: rowLineHeight,
                                    }, children: option.displayLabel || option.label })] }, `${section.id}-${option.id}`));
                    }), (() => {
                        const contextAction = contextActionByCategory.get(section.category);
                        if (!contextAction) {
                            return null;
                        }
                        const globalIndex = runningIndex;
                        runningIndex += 1;
                        const contextEntryKey = getContextActionEntryKey(section.id, contextAction.id);
                        const isSelected = selectedEntryKey
                            ? selectedEntryKey === contextEntryKey
                            : globalIndex === selectedIndex;
                        const contextCategory = contextAction.category || section.category;
                        const contextLabel = contextAction.displayLabel || contextAction.label;
                        return (_jsxs("div", { className: `flex items-center cursor-pointer transition-colors ${isSelected ? "bg-gray-200" : "hover:bg-gray-100"}`, style: {
                                minHeight: itemMinHeight,
                                columnGap: `${itemRowGapPx}px`,
                                padding: `${itemRowPaddingYPx}px ${itemRowPaddingXPx}px`,
                                borderRadius: `${itemRowBorderRadiusPx}px`,
                            }, onMouseDown: (event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onSelect(contextAction);
                            }, onMouseEnter: () => {
                                if (onEntryHover) {
                                    onEntryHover(contextEntryKey);
                                    return;
                                }
                                onSelectedIndexChange?.(globalIndex);
                            }, children: [_jsx("div", { className: "flex items-center justify-center", style: {
                                        width: itemIconBoxWidthPx,
                                        height: itemIconBoxHeightPx,
                                    }, children: renderCategoryIcon(contextCategory, contextAction, iconSizePx) }), _jsxs("div", { className: "flex-1 min-w-0 relative", children: [_jsx("span", { className: "truncate font-bold text-gray-990 block", style: {
                                                fontFamily: "Mulish, sans-serif",
                                                fontSize: itemFontSizePx,
                                                lineHeight: rowLineHeight,
                                                paddingRight: `${selectedTagReservePx}px`,
                                            }, children: contextLabel }), _jsx("span", { className: "inline-flex items-center rounded-full border uppercase tracking-wide whitespace-nowrap pointer-events-none", style: {
                                                fontFamily: "Mulish, sans-serif",
                                                fontSize: selectedTagFontSizePx,
                                                fontWeight: 700,
                                                lineHeight: 1.2,
                                                padding: selectedTagPadding,
                                                borderColor: "#86EFAC",
                                                color: "#166534",
                                                backgroundColor: "#ECFDF5",
                                                position: "absolute",
                                                right: 0,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                            }, children: "Currently selected" })] })] }, `${section.id}-context-${contextAction.id}`));
                    })(), section.category === "file" &&
                        onUploadClick &&
                        (() => {
                            const globalIndex = runningIndex;
                            runningIndex += 1;
                            const uploadEntryKey = getUploadEntryKey(section.id);
                            const isSelected = selectedEntryKey
                                ? selectedEntryKey === uploadEntryKey
                                : globalIndex === selectedIndex;
                            return (_jsxs("div", { className: `flex items-center cursor-pointer transition-colors ${isSelected ? "bg-gray-200" : "hover:bg-gray-100"}`, style: {
                                    minHeight: itemMinHeight,
                                    columnGap: `${itemRowGapPx}px`,
                                    padding: `${itemRowPaddingYPx}px ${itemRowPaddingXPx}px`,
                                    borderRadius: `${itemRowBorderRadiusPx}px`,
                                }, onMouseDown: (event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onUploadClick();
                                }, onMouseEnter: () => {
                                    if (onEntryHover) {
                                        onEntryHover(uploadEntryKey);
                                        return;
                                    }
                                    onSelectedIndexChange?.(globalIndex);
                                }, children: [_jsx("div", { className: "flex items-center justify-center", style: {
                                            width: itemIconBoxWidthPx,
                                            height: itemIconBoxHeightPx,
                                        }, children: _jsx(UploadIcon, { size: iconSizePx, color: "#374151" }) }), _jsx("span", { className: "flex-1 font-bold text-gray-990 truncate", style: {
                                            fontFamily: "Mulish, sans-serif",
                                            fontSize: itemFontSizePx,
                                            lineHeight: rowLineHeight,
                                        }, children: "Upload a file" })] }, `${section.id}-upload`));
                        })()] }, section.id))), isLoading && (_jsx("div", { className: "px-2 py-4 text-center text-gray-500", style: { fontFamily: "Mulish, sans-serif", fontSize: itemFontSizePx }, children: "Searching..." })), !isLoading && itemCount === 0 && (_jsx("div", { className: "px-2 py-4 text-center text-gray-500", style: { fontFamily: "Mulish, sans-serif", fontSize: itemFontSizePx }, children: "No results found" }))] }));
};
MentionMenu.displayName = "MentionMenu";
